import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth";
import { AuthBanner } from "@/components/auth/AuthBanner";
import { SignupCompleteBanner } from "@/components/auth/SignupCompleteBanner";
import { OAuthButtons } from "@/components/OAuthButtons";
import { OAuthDivider } from "@/components/OAuthDivider";

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): { next?: string } => {
    if (typeof search.next === "string") return { next: search.next };
    return {};
  },
  component: LoginPage,
});

interface StrengthResult {
  label: string;
  color: string;
}

function scorePassword(v: string): StrengthResult {
  if (!v) return { label: "awaiting input", color: "var(--color-fg-3)" };
  let s = 0;
  if (v.length >= 8) s++;
  if (v.length >= 12) s++;
  if (/[A-Z]/.test(v) && /[a-z]/.test(v)) s++;
  if (/\d/.test(v)) s++;
  if (/[^A-Za-z0-9]/.test(v)) s++;
  const map: StrengthResult[] = [
    { label: "too short", color: "var(--color-death)" },
    { label: "weak", color: "var(--color-death)" },
    { label: "okay", color: "var(--color-signal)" },
    { label: "strong", color: "var(--color-data)" },
    { label: "core stable", color: "var(--color-energy)" },
    { label: "fortified", color: "var(--color-energy)" },
  ];
  return map[Math.min(s, 5)];
}

function MailIcon() {
  return (
    <svg
      className="access-icon-leading"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M2 7l10 7 10-7" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      className="access-icon-leading"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      className="access-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function BoltIcon() {
  return (
    <svg className="access-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg
      className="access-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      className="access-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}

function LoginPage() {
  const { next } = Route.useSearch();
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginVisible, setLoginVisible] = useState(false);
  const [keepBooted, setKeepBooted] = useState(true);
  const [loginError, setLoginError] = useState<
    null | "bad_credentials" | "email_not_verified" | "unknown"
  >(null);
  const [loginPending, setLoginPending] = useState(false);

  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupVisible, setSignupVisible] = useState(false);
  const [terms, setTerms] = useState(false);
  const [signupError, setSignupError] = useState<
    null | "email_taken" | "weak_password" | "invalid_email" | "unknown"
  >(null);
  const [signupPending, setSignupPending] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);

  const emailFilled = signupEmail.length > 0;
  const signupValid = emailFilled && signupPassword.length >= 8;
  const strength = useMemo(() => scorePassword(signupPassword), [signupPassword]);

  async function handleLoginSubmit() {
    setLoginError(null);
    setLoginPending(true);
    try {
      const res = await signIn(loginEmail, loginPassword);
      if (!res.ok) {
        setLoginError(res.error ?? "unknown");
        return;
      }
      await navigate({ to: "/" });
    } finally {
      setLoginPending(false);
    }
  }

  async function handleSignupSubmit() {
    if (!signupValid || !terms) return;
    setSignupError(null);
    setSignupPending(true);
    try {
      const res = await signUp(signupEmail, signupPassword);
      if (!res.ok) {
        setSignupError(res.error ?? "unknown");
        return;
      }
      setSignupComplete(true);
    } finally {
      setSignupPending(false);
    }
  }

  if (signupComplete) {
    return <SignupCompleteBanner onReturnToLogin={() => setSignupComplete(false)} />;
  }

  return (
    <div className="access-stage">
      <span className="access-corner-ticks" aria-hidden="true">
        <span className="tl" />
        <span className="tr" />
        <span className="bl" />
        <span className="br" />
      </span>

      <section className="access-panel access-panel--login" aria-labelledby="login-title">
        <div className="access-panel__eyebrow">
          <span className="num">01</span>
          <span>RETURNING</span>
          <span className="access-panel__eyebrow-label">· Log in</span>
        </div>
        <h1 className="access-panel__title" id="login-title">
          Resume your session.
        </h1>
        <p className="access-panel__voice">
          I remember you. Sign in and I&rsquo;ll get back to work.
        </p>

        <form
          className="access-form"
          id="login-form"
          autoComplete="on"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void handleLoginSubmit();
          }}
        >
          <OAuthButtons next={next} />
          <OAuthDivider />
          {loginError === "email_not_verified" && (
            <AuthBanner
              tone="caution"
              title="I haven't seen you confirm yet."
              voice="Check your mail for the boot key."
              action={{
                label: "RESEND",
                onClick: () => {},
                disabled: true,
                tooltip: "coming soon",
              }}
            />
          )}
          {(loginError === "bad_credentials" || loginError === "unknown") && (
            <AuthBanner
              tone="death"
              title={
                loginError === "bad_credentials" ? "That pair didn’t match." : "Something stalled."
              }
              voice="Try again."
            />
          )}
          <div className="access-field">
            <label className="access-field__label" htmlFor="login-email">
              <span>EMAIL</span>
              <span className="meta">required</span>
            </label>
            <div className="access-field__wrap">
              <input
                id="login-email"
                type="email"
                name="email"
                placeholder="you@core.local"
                autoComplete="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
              />
              <MailIcon />
            </div>
          </div>

          <div className="access-field">
            <label className="access-field__label" htmlFor="login-password">
              <span>PASSWORD</span>
              <a href="#" className="access-link access-link--small">
                FORGOT
              </a>
            </label>
            <div className="access-field__wrap">
              <input
                id="login-password"
                type={loginVisible ? "text" : "password"}
                name="password"
                placeholder="••••••••••"
                autoComplete="current-password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
              />
              <LockIcon />
              <button
                type="button"
                className="access-icon-trailing"
                aria-label={loginVisible ? "Hide password" : "Show password"}
                onClick={() => setLoginVisible((v) => !v)}
              >
                <EyeIcon />
              </button>
            </div>
          </div>

          <div className="access-row-between">
            <label className="access-checkbox">
              <input
                type="checkbox"
                name="remember"
                checked={keepBooted}
                onChange={(e) => setKeepBooted(e.target.checked)}
              />
              <span className="access-checkbox__box">
                <CheckIcon />
              </span>
              <span>keep me booted</span>
            </label>
            <span className="access-meta">
              <span>LAST SEEN</span>
              <span style={{ color: "var(--color-fg-2)" }}>2D 14H AGO</span>
            </span>
          </div>

          <button type="submit" className="access-btn access-btn--energy" disabled={loginPending}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              <BoltIcon />
              {loginPending ? "BOOTING…" : "BOOT SESSION"}
            </span>
            <span className="access-btn__kbd">&#x21B5;</span>
          </button>
        </form>
      </section>

      <div className="access-divider" role="separator" aria-orientation="vertical">
        <div className="access-divider__or">OR</div>
      </div>

      <section
        className="access-panel access-panel--signup"
        aria-labelledby="signup-title"
        data-email-filled={emailFilled ? "true" : "false"}
      >
        <div className="access-panel__eyebrow">
          <span className="num">02</span>
          <span style={{ color: "var(--color-data)" }}>NEW</span>
          <span className="access-panel__eyebrow-label access-panel__eyebrow-label--data">
            · Sign up
          </span>
        </div>
        <h1 className="access-panel__title" id="signup-title">
          Wake a new automaton.
        </h1>
        <p className="access-panel__voice">Hello. I&rsquo;m new. Tell me where to find you.</p>

        <form
          className="access-form"
          id="signup-form"
          autoComplete="on"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void handleSignupSubmit();
          }}
        >
          <OAuthButtons next={next} />
          <OAuthDivider />
          <div className="access-field">
            <label className="access-field__label" htmlFor="signup-email">
              <span>EMAIL</span>
              <span className="meta">we&rsquo;ll send a boot key</span>
            </label>
            <div className="access-field__wrap">
              <input
                id="signup-email"
                type="email"
                name="email"
                placeholder="you@core.local"
                autoComplete="email"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
              />
              <MailIcon />
            </div>
          </div>

          <div
            className="access-reveal"
            id="signup-reveal"
            aria-hidden={emailFilled ? "false" : "true"}
          >
            <div className="access-reveal__inner">
              <div className="access-field" style={{ paddingTop: 4 }}>
                <label className="access-field__label" htmlFor="signup-password">
                  <span>SET PASSWORD</span>
                  <span className="meta">12+ chars recommended</span>
                </label>
                <div className="access-field__wrap">
                  <input
                    id="signup-password"
                    type={signupVisible ? "text" : "password"}
                    name="password"
                    placeholder="generate or invent"
                    autoComplete="new-password"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                  />
                  <LockIcon />
                  <button
                    type="button"
                    className="access-icon-trailing"
                    aria-label={signupVisible ? "Hide password" : "Show password"}
                    onClick={() => setSignupVisible((v) => !v)}
                  >
                    <EyeIcon />
                  </button>
                </div>
                <div
                  className="access-reveal__hint"
                  id="strength-hint"
                  style={{ color: strength.color }}
                >
                  <span style={{ color: strength.color, display: "inline-flex" }}>
                    <InfoIcon />
                  </span>
                  <span data-testid="strength-text">{strength.label}</span>
                </div>
              </div>
            </div>
          </div>

          <label className="access-checkbox" style={{ marginTop: 2 }}>
            <input
              type="checkbox"
              name="terms"
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
            />
            <span className="access-checkbox__box">
              <CheckIcon />
            </span>
            <span>
              I accept that the automaton can die.{" "}
              <a href="#" className="access-link">
                terms
              </a>
            </span>
          </label>

          <button
            type="submit"
            className="access-btn access-btn--data"
            id="signup-submit"
            disabled={!signupValid || signupPending}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              <ArrowIcon />
              {signupPending ? "WAKING…" : "WAKE IT UP"}
            </span>
            <span className="access-btn__kbd">&#x21B5;</span>
          </button>
          {signupError && (
            <p className="auth-signup-error" role="alert">
              {signupError === "email_taken" && "That address is already registered."}
              {signupError === "weak_password" && "Password is too weak."}
              {signupError === "invalid_email" && "That doesn't look like a valid address."}
              {signupError === "unknown" && "Something stalled. Try again."}
            </p>
          )}
        </form>
      </section>
    </div>
  );
}
